import { useQuery } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';
import { RadioGroup } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import { SearchableTableLayout } from 'components/common/layout/searchable-table';
import { Table } from 'components/common/table';
import { useNavTableConnection } from 'hooks/use-nav-table-connection';
import { useQueryParamState } from 'hooks/use-query-params';
import { FC } from 'react';
import { useParams } from 'react-router';
import {
  CampaignAgentPhones as ICampaignAgentPhones,
  CampaignAgentPhonesVariables,
} from 'types/apollo/CampaignAgentPhones';
import { AgentPhoneStatus } from 'types/apollo/globalTypes';
import { CampaignAgentPhonesQuery } from './campaign-agent-phones.graphql';
import { applyFilter, FilterStatus } from './filter';

export interface Data {
  id: string;
  phone: string;
  messagingServiceId: string | null;
  locality: string;
  region: string;
  status: AgentPhoneStatus;
}

const statusToDisplayStringMap = new Map<FilterStatus, string>([
  ['ALL', 'All'],
  [AgentPhoneStatus.ACTIVE, 'Active'],
  [AgentPhoneStatus.INACTIVE, 'Inactive'],
  [AgentPhoneStatus.DRAINING, 'Draining'],
]);

export const CampaignAgentPhones: FC = () => {
  const { campaignId: id } = useParams();
  const [search, setSearch] = useQueryParamState('search', '');
  const [statusFilter, setStatusFilter] = useQueryParamState('status', 'ALL');

  const { data, loading } = useQuery<ICampaignAgentPhones, CampaignAgentPhonesVariables>(
    CampaignAgentPhonesQuery,
    {
      variables: { campaignId: id ?? '0' },
    },
  );

  const tableData = useNavTableConnection<Data>([
    {
      id: 'phone',
      label: 'Phone',
      numeric: false,
    },
    {
      id: 'messagingServiceId',
      label: 'Messaging Service',
      numeric: false,
    },
    {
      id: 'locality',
      label: 'Locality',
      numeric: false,
    },
    {
      id: 'region',
      label: 'Region',
      numeric: false,
    },
    {
      id: 'status',
      label: 'Status',
      numeric: false,
      renderer: (row) => statusToDisplayStringMap.get(row.status),
    },
  ]);

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter((event.target as HTMLInputElement).value as FilterStatus);
  };

  const tableRows = data?.campaign?.agentPhones
    ? applyFilter({ search, status: statusFilter }, data.campaign.agentPhones)
    : [];

  return (
    <SearchableTableLayout>
      <SearchableTableLayout.FiltersWrapper>
        <TextField
          name="search"
          label="Search by Name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl>
          <FormLabel>Status</FormLabel>
          <RadioGroup
            aria-label="Status"
            name="statusFilter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{ flexDirection: { xs: 'row', lg: 'column' } }}
          >
            <FormControlLabel
              value="ALL"
              control={<Radio />}
              label={statusToDisplayStringMap.get('ALL') || ''}
            />
            <FormControlLabel
              value={AgentPhoneStatus.ACTIVE}
              control={<Radio />}
              label={statusToDisplayStringMap.get(AgentPhoneStatus.ACTIVE) || ''}
            />
            <FormControlLabel
              value={AgentPhoneStatus.INACTIVE}
              control={<Radio />}
              label={statusToDisplayStringMap.get(AgentPhoneStatus.INACTIVE) || ''}
            />
            <FormControlLabel
              value={AgentPhoneStatus.DRAINING}
              control={<Radio />}
              label={statusToDisplayStringMap.get(AgentPhoneStatus.DRAINING) || ''}
            />
          </RadioGroup>
        </FormControl>
      </SearchableTableLayout.FiltersWrapper>
      <SearchableTableLayout.TableWrapper>
        <Table loading={loading} rows={tableRows} {...tableData} />
      </SearchableTableLayout.TableWrapper>
    </SearchableTableLayout>
  );
};
